import { useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, BookUser, AlertTriangle } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function BuyerLedger() {
  const { data: buyersRaw } = useResource("/buyers");
  const buyers = Array.isArray(buyersRaw) ? buyersRaw : [];
  const [tab, setTab] = useState("ledger");
  const [buyerId, setBuyerId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [applied, setApplied] = useState({ buyerId: "", dateFrom: "", dateTo: "" });

  const qs = useMemo(() => {
    if (!applied.buyerId) return "";
    const p = new URLSearchParams({ buyerId: applied.buyerId });
    if (applied.dateFrom) p.set("dateFrom", applied.dateFrom);
    if (applied.dateTo) p.set("dateTo", applied.dateTo);
    return `?${p.toString()}`;
  }, [applied]);

  const { data } = useResource(qs ? `/buyer-ledger${qs}` : null);
  const { data: ageRaw } = useResource(tab === "ageing" ? "/receivable-ageing" : null);
  const age = ageRaw || {};
  const ageRows = age.rows || [];
  const ageTotals = age.totals || {};
  const L = data || {};
  const [ledgerSearch, setLedgerSearch] = useState("");
  const allRows = L.rows || [];
  const rows = ledgerSearch.trim()
    ? allRows.filter((r) => `${r.docNo || ""} ${r.reference || ""}`.toLowerCase().includes(ledgerSearch.trim().toLowerCase()))
    : allRows;
  const t = L.totals || {};

  const openBuyerLedger = (bid) => { setTab("ledger"); setBuyerId(bid); setApplied({ buyerId: bid, dateFrom: "", dateTo: "" }); };

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/buyer-ledger/export${qs}&format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `buyer-ledger.${fmt === "excel" ? "xlsx" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };
  const doAgeExport = async (fmt) => {
    try {
      const res = await api.get(`/receivable-ageing/export?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `receivable-ageing.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };

  const AGE_COLS = [["notDue", "Not Due"], ["d0_30", "0–30"], ["d31_60", "31–60"], ["d61_90", "61–90"], ["d91_180", "91–180"], ["d180_plus", "180+"]];

  return (
    <div className="space-y-6">
      <PageHeader title="Buyer Ledger & Ageing" subtitle="Opening + Sales − Credit Notes − Receipts = Outstanding · sorted by invoice date">
        {tab === "ledger" && qs && (
          <div className="flex items-center gap-2">
            <Input data-testid="ledger-search" value={ledgerSearch} onChange={(e) => setLedgerSearch(e.target.value)} placeholder="Search invoice / receipt / note #" className="h-8 w-56 text-xs" />
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="ledger-export-pdf" onClick={() => doExport("pdf")}><Download className="h-4 w-4" /> PDF</Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="ledger-export-excel" onClick={() => doExport("excel")}><Download className="h-4 w-4" /> Excel</Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="ledger-export-csv" onClick={() => doExport("csv")}><Download className="h-4 w-4" /> CSV</Button>
          </div>
        )}
        {tab === "ageing" && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="age-export-excel" onClick={() => doAgeExport("excel")}><Download className="h-4 w-4" /> Excel</Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="age-export-pdf" onClick={() => doAgeExport("pdf")}><Download className="h-4 w-4" /> PDF</Button>
          </div>
        )}
      </PageHeader>

      <div className="flex gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm w-fit">
        <button data-testid="bl-tab-ledger" onClick={() => setTab("ledger")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${tab === "ledger" ? "bg-amber-700 text-white" : "text-stone-600"}`}>Ledger</button>
        <button data-testid="bl-tab-ageing" onClick={() => setTab("ageing")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${tab === "ageing" ? "bg-amber-700 text-white" : "text-stone-600"}`}>Receivable Ageing</button>
      </div>

      {tab === "ageing" && (
        <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="ageing-table">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500">
              <tr><th className="px-3 py-2.5">Buyer</th><th className="px-3 py-2.5">Terms</th>{AGE_COLS.map(([, l]) => <th key={l} className="px-3 py-2.5 text-right">{l}</th>)}<th className="px-3 py-2.5 text-right">Total</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {ageRows.length === 0 ? <tr><td colSpan={9} className="px-3 py-6 text-center text-stone-400">No outstanding receivables.</td></tr> :
                ageRows.map((r) => (
                  <tr key={r.buyerId} data-testid={`age-row-${r.buyerId}`} className="cursor-pointer hover:bg-amber-50/50" onClick={() => openBuyerLedger(r.buyerId)}>
                    <td className="px-3 py-2 font-medium text-amber-800 underline-offset-2 hover:underline">{r.buyerName}</td>
                    <td className="px-3 py-2 text-xs text-stone-500">{r.paymentTermsDays}d</td>
                    {AGE_COLS.map(([k]) => <td key={k} className="px-3 py-2 text-right tabular-nums">{r[k] ? money(r[k]) : "—"}</td>)}
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{money(r.total)}</td>
                  </tr>
                ))}
            </tbody>
            {ageRows.length > 0 && (
              <tfoot className="bg-stone-50 font-semibold"><tr><td className="px-3 py-2">TOTAL</td><td />{AGE_COLS.map(([k]) => <td key={k} className="px-3 py-2 text-right tabular-nums">{money(ageTotals[k])}</td>)}<td className="px-3 py-2 text-right tabular-nums text-amber-700">{money(ageTotals.total)}</td></tr></tfoot>
            )}
          </table>
        </div>
      )}

      {tab === "ledger" && (
      <>
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <div><Label className="text-xs">Buyer</Label>
          <select data-testid="ledger-buyer" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="h-10 w-64 rounded-md border border-stone-200 bg-white px-3 text-sm">
            <option value="">Select buyer…</option>
            {buyers.map((b) => <option key={b.id} value={b.buyerId || b.id}>{b.name} · {b.buyerId}</option>)}
          </select></div>
        <div><Label className="text-xs">From (invoice date)</Label><Input type="date" data-testid="ledger-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" /></div>
        <div><Label className="text-xs">To (invoice date)</Label><Input type="date" data-testid="ledger-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" /></div>
        <Button data-testid="ledger-apply" className="bg-amber-700 hover:bg-amber-800" disabled={!buyerId} onClick={() => setApplied({ buyerId, dateFrom, dateTo })}>View Ledger</Button>
      </div>

      {L.buyer && (
        <>
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-stone-800"><BookUser className="h-4 w-4 text-amber-700" />
              <span className="font-display text-base font-bold uppercase">{L.buyer.name}</span>
              <span className="font-mono text-xs text-stone-500">{L.buyer.buyerId} · GSTIN {L.buyer.gstin || "—"}</span>
            </div>
            <div className="mt-1 text-xs text-stone-500">{L.buyer.address}</div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-6">
              {[["Invoices", t.invoices], ["Total Sales", money(t.sales)], ["Credit Notes", money(t.creditNotes)], ["Receipts (Bank/Cash)", money(t.receipts)], ["TDS Deducted", money(t.tds)], ["Closing Outstanding", money(t.closing)]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2"><div className="font-mono text-base font-bold text-stone-900">{v}</div><div className="text-[10px] font-semibold uppercase text-stone-500">{k}</div></div>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="ledger-table">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500">
                <tr>{["Date", "Type", "Document No", "Source", "Debit", "Credit", "Balance"].map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-6 text-center text-stone-400">No transactions in range.</td></tr> :
                  rows.map((r, i) => (
                    <tr key={i} className="hover:bg-stone-50/60" data-testid={`ledger-row-${i}`}>
                      <td className="px-4 py-2">{r.date}</td><td className="px-4 py-2">{r.type}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.docNo}</td>
                      <td className="px-4 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${r.source === "TALLY IMPORT" ? "border-sky-200 bg-sky-50 text-sky-700" : r.source === "RECEIPT" ? "border-violet-200 bg-violet-50 text-violet-700" : r.source === "TDS" ? "border-amber-300 bg-amber-50 text-amber-800" : r.source === "CREDIT_NOTE" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{r.source === "CREDIT_NOTE" ? "CREDIT NOTE" : r.source}</span></td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.debit ? money(r.debit) : "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-violet-700">{r.credit ? money(r.credit) : "—"}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{money(r.balance)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}
